import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environment/environment';

/**
 * HTTP access to the ticketing API: listing, searching, sorting, paging,
 * creating, replying to and reopening tickets.
 *
 * Every call authenticates from `sessionStorage` rather than from an argument —
 * `emailAddress`, `auth_token` and (for list endpoints) `ecryptedEMail`, which is
 * sent as the `X-Frame-edi` header. Calls throw synchronously when any of those
 * are absent, so callers must be ready for a throw as well as an error response.
 */
@Injectable({
  providedIn: 'root'
})
export class TicketService {
  /**
   * CC recipients staged by {@link TicketDetailsComponent} and attached to the next
   * {@link TicketService.replyOnTicket} call. Shared mutable state, not per-request.
   */
  participantsList: string[] = []

  constructor(private readonly http: HttpClient) {
  }

  /**
   * Base API URL for the current domain.
   *
   * Returns the live URL when the `Domain` session code is `L`, otherwise the
   * default ticketing API.
   *
   * Public so that callers posting to sibling endpoints on the same API - the
   * rich text editor's inline image upload in `HelpDeskComponent` - resolve the
   * host the same way every request in this service does, rather than naming
   * one of the two environments themselves.
   */
  get ticketAPI(): string {
    const domain = sessionStorage.getItem('Domain');
    if (domain === 'L') {
      return environment.liveURL;
    } else {
      return environment.ticketAPI;
    }
  }

  /**
   * Fetches the ticket count for a status.
   *
   * @param status Status to count, e.g. `In-Progress` or `All`.
   * @returns Response whose `count` holds the total for that status.
   * @throws If the required session data is missing, meaning the session expired.
   */
  getTicketsCount(status: string): Observable<any> {
    const email = sessionStorage.getItem('emailAddress');
    const authToken = sessionStorage.getItem('auth_token');
    const encryptedEmail = sessionStorage.getItem('ecryptedEMail');

    if (!email || !authToken || !encryptedEmail) {
      console.error('[TicketService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }
    return this.http.post(
      `${this.ticketAPI}/ticket/`,
      {
        "email": email,
        "status": `${status}`
      },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`,
          'X-Frame-edi': encryptedEmail
        }),
      }
    )
  }

  /**
   * Fetches the first page of tickets for a status.
   *
   * @param status Status to filter by, e.g. `In-Progress` or `All`.
   * @param page_size Rows per page.
   * @returns Paged response with `count`, `next`, `previous` and `results`.
   * @throws If the required session data is missing, meaning the session expired.
   */
  getTicketsData(status: string, page_size: number): Observable<any> {
    const email = sessionStorage.getItem('emailAddress');
    const authToken = sessionStorage.getItem('auth_token');
    const encryptedEmail = sessionStorage.getItem('ecryptedEMail');

    if (!email || !authToken || !encryptedEmail) {
      console.error('[TicketService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }

    const params = new HttpParams().set('page_size', page_size);

    return this.http.post(
      `${this.ticketAPI}/ticket/`,
      {
        "email": email,
        "status": `${status}`
      },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`,
          'X-Frame-edi': encryptedEmail
        }),
        params: params
      }
    )
  }

  /**
   * Follows a pagination link returned by a previous status-filtered listing.
   *
   * @param url Absolute `next` or `previous` URL from the prior response.
   * @param status Status the original listing was filtered by.
   * @returns The requested page, in the same shape as {@link getTicketsData}.
   * @throws If the required session data is missing, meaning the session expired.
   */
  nextTicketData(url: string, status: string): Observable<any> {
    const email = sessionStorage.getItem('emailAddress');
    const authToken = sessionStorage.getItem('auth_token');
    const encryptedEmail = sessionStorage.getItem('ecryptedEMail');

    if (!email || !authToken || !encryptedEmail) {
      console.error('[TicketService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }

    return this.http.post(
      url,
      {
        "email": email,
        "status": `${status}`
      },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`,
          'X-Frame-edi': encryptedEmail
        }),
      }
    )
  }

  /**
   * Follows a pagination link returned by a previous sorted listing.
   *
   * Sorted listings page through a different endpoint than status-filtered ones,
   * so the sort field and direction have to be resent with every page.
   *
   * @param url Absolute `next` or `previous` URL from the prior response.
   * @param filter Field sorted on, e.g. `ticket_id` or `last_updated`.
   * @param order Sort direction, `asc` or `desc`.
   * @returns The requested page, in the same shape as {@link sortTicketsData}.
   * @throws If the required session data is missing, meaning the session expired.
   */
  nextTicketDataSort(url: string, filter: string, order: string):Observable<any>  {
    const email = sessionStorage.getItem('emailAddress');
    const authToken = sessionStorage.getItem('auth_token');
    const encryptedEmail = sessionStorage.getItem('ecryptedEMail');

    if (!email || !authToken || !encryptedEmail) {
      console.error('[TicketService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }

    return this.http.post(
      url,
      {
        "email": email,
        "filter": `${filter}`,
        "sort_by": `${order}`
      },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`,
          'X-Frame-edi': encryptedEmail
        }),
      }
    ) 
  }

  /**
   * Fetches the first page of tickets ordered by a field.
   *
   * @param filter Field to sort on, e.g. `ticket_id` or `last_updated`.
   * @param order Sort direction, `asc` or `desc`.
   * @param page_size Rows per page.
   * @returns Paged response with `count`, `next`, `previous` and `results`.
   * @throws If the required session data is missing, meaning the session expired.
   */
  sortTicketsData(filter: string, order: string, page_size: number): Observable<any> {
    const email = sessionStorage.getItem('emailAddress');
    const authToken = sessionStorage.getItem('auth_token');
    const encryptedEmail = sessionStorage.getItem('ecryptedEMail');

    if (!email || !authToken || !encryptedEmail) {
      console.error('[TicketService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }

    const params = new HttpParams().set('page_size', page_size);

    return this.http.post(
      `${this.ticketAPI}/ticket/sort/`,
      {
        "email": email,
        "filter": `${filter}`,
        "sort_by": `${order}`
      },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`,
          'X-Frame-edi': encryptedEmail
        }),
        params: params
      }
    )
  }

  /**
   * Fetches the full email thread for one ticket.
   *
   * @param ticket_id Ticket to load.
   * @returns The ordered list of emails making up the conversation.
   * @throws If the required session data is missing, meaning the session expired.
   */
  getTicketDetails(ticket_id: string): Observable<any> {
    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
      console.error('[TicketService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }

    return this.http.post(
      `${this.ticketAPI}/ticket/details/`,
      {
        "ticket_id": `${ticket_id}`
      },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`
        })
      }
    )
  }

  /**
   * Swaps editor image previews for the URL each image was uploaded to.
   *
   * The editors insert the picked file as a local `blob:` preview, because the
   * storage account the upload returns refuses anonymous reads and so cannot be
   * shown while composing. The upload URL rides along on the element in
   * `data-send-src` until here, the last point before the body leaves the
   * browser, where it goes back into `src` and the marker is dropped.
   *
   * @param html Reply or ticket body as HTML from the rich text editor.
   * @returns The body with every previewed image pointing at its uploaded URL.
   */
  private restoreUploadedImageSources(html: string): string {
    return html.replace(
      /<img\b([^>]*?)\sdata-send-src=["']([^"']+)["']([^>]*)>/gi,
      (_match: string, before: string, uploadedUrl: string, after: string) => {
        const attributes = `${before}${after}`.replace(/\ssrc=["'][^"']*["']/i, '');
        return `<img src="${uploadedUrl}"${attributes}>`;
      }
    );
  }

  /**
   * Posts a reply onto an existing ticket, with any staged CC list and attachments.
   *
   * Inline images are normalised before sending: previews are pointed back at the
   * URL they were uploaded to, any `blob:` URL left over after that is hidden with
   * a `display-none` class since it would be a dead link once the message leaves
   * the browser, and images already persisted to blob storage have a stale inline
   * `display: none` stripped so they render in the delivered email.
   *
   * CC recipients come from {@link TicketService.participantsList}, not a parameter.
   *
   * @param ticket_id Ticket being replied to.
   * @param email_body Reply body as HTML from the rich text editor.
   * @param attachment_list Files to attach.
   * @returns The API response for the reply.
   * @throws If the required session data is missing, meaning the session expired.
   */
  replyOnTicket(ticket_id: string, email_body: string, attachment_list: File[]): Observable<any> {

    let updatedHtml = this.restoreUploadedImageSources(email_body).replace(
      /<img[^>]+src=["']blob:http[^"']+["'][^>]*>/gi,
      match => match.replace('<img', '<img class="display-none"')
    );

     // Then, show uploaded images from the blob storage and remove display: none
    updatedHtml = updatedHtml.replace(
      /<img([^>]*src=["']https:\/\/trplprodhelpdesk\.blob\.core\.windows\.net\/static[^"']+["'][^>]*)>/gi,
      (_, imgAttributes) => {
        // Remove display: none !important; from style attribute
        const updatedAttributes = imgAttributes.replace(/display:\s*none\s*!important;\s*/gi, '');
        return `<img${updatedAttributes}>`;
      }
    );

    const email = sessionStorage.getItem('emailAddress');
    const authToken = sessionStorage.getItem('auth_token');

    if (!email || !authToken) {
      console.error('[TicketService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }

    const formData = new FormData()
    formData.append('email', email)
    formData.append('cc_list', JSON.stringify(this.participantsList))
    formData.append('email_body', updatedHtml)
    formData.append('ticket_id', ticket_id)
    for (const file of attachment_list) {
      formData.append('attachment_list', file, file.name);
    }

    return this.http.post(
      `${this.ticketAPI}/ticket/reply/`,
      formData,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`
        })
      }
    )
  }

  /**
   * Creates a new ticket.
   *
   * Applies the same inline-image normalisation as {@link replyOnTicket}, and sends
   * `portal_flag` so the backend records the ticket as raised from the portal.
   *
   * @param email_subject Ticket subject.
   * @param cc_list Additional recipients to copy.
   * @param email_body Ticket body as HTML from the rich text editor.
   * @param attachment_list Files to attach.
   * @returns Response whose `results` reads `Ticket created successfully.` on success.
   * @throws If the required session data is missing, meaning the session expired.
   */
  createTicket(email_subject: string, cc_list: string[], email_body: string, attachment_list: File[]): Observable<any> {

    const email = sessionStorage.getItem('emailAddress');
    const authToken = sessionStorage.getItem('auth_token');

    let updatedHtml = this.restoreUploadedImageSources(email_body).replace(
      /<img[^>]+src=["']blob:http[^"']+["'][^>]*>/gi,
      match => match.replace('<img', '<img class="display-none"')
    );

    updatedHtml = updatedHtml.replace(
      /<img([^>]*src=["']https:\/\/trplprodhelpdesk\.blob\.core\.windows\.net\/static[^"']+["'][^>]*)>/gi,
      (_, imgAttributes) => {
        const updatedAttributes = imgAttributes.replace(/display:\s*none\s*!important;\s*/gi, '');
        return `<img${updatedAttributes}>`;
      }
    );

    if (!email || !authToken) {
      console.error('[TicketService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }

    const formData = new FormData()
    formData.append('email', email)
    formData.append('email_subject', email_subject)
    formData.append('cc_list', JSON.stringify(cc_list))
    formData.append('email_body', updatedHtml);
    formData.append('portal_flag', '1')
    for (const file of attachment_list) {
      formData.append('attachment_list', file, file.name);
    }

    return this.http.post(
      `${this.ticketAPI}/ticket/create/`,
      formData,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`
        })
      }
    )
  }

  /**
   * Searches the caller's tickets.
   *
   * An all-digits term is treated as a ticket ID; anything else is matched against
   * the subject.
   *
   * @param searchString Ticket ID or subject fragment to search for.
   * @returns Paged response with the matching tickets in `results`.
   * @throws If the required session data is missing, meaning the session expired.
   */
  searchTicket(searchString: string): Observable<any> {
    const email = sessionStorage.getItem('emailAddress');
    const authToken = sessionStorage.getItem('auth_token');
    const encryptedEmail = sessionStorage.getItem('ecryptedEMail');

    if (!email || !authToken || !encryptedEmail) {
      console.error('[TicketService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }

    let body: any = {
      "email": email,
    };

    if (/^\d+$/.test(searchString)) {
      body["ticket_id"] = parseInt(searchString, 10);
    } else {
      body["subject"] = searchString;
    }

    return this.http.post(
      `${this.ticketAPI}/ticket/search/`,
      body,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`,
          'X-Frame-edi': encryptedEmail
        })
      }
    )
  }

  /**
   * Reopens a resolved or closed ticket.
   *
   * @param ticketID Ticket to reopen.
   * @returns The API response for the reopen request.
   * @throws If the required session data is missing, meaning the session expired.
   */
  reopenTicket(ticketID: string): Observable<any> {
    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
      console.error('[TicketService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }

    const formData = new FormData()

    formData.append('ticket_id', ticketID)

    return this.http.post(
      `${this.ticketAPI}/ticket/reopen/`,
      formData,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`
        })
      }
    )
  }

}
